import React, { useLayoutEffect, useRef, useState } from 'react'
import { convertToCurrency } from './../../../../shared/formatters/convertToCurrency.formatter'
import { convertToCurrencyWithoutSign } from './../../../../shared/formatters/convertToCurrencyWithoutSign.formatter'
import { getHeadshot, getEliteAgencyTeamLogo } from './../../../../shared/utilities/getHeadshot.function'
import './BigScreenBoard.scss'

const formatTotal = (value, unit) => {
  const num = parseFloat(value)
  if (isNaN(num)) return value
  switch (unit) {
    case 'points':
      return convertToCurrencyWithoutSign(num)
    case 'av':
      return convertToCurrency(Math.round(num)).replace(/\.00$/, '')
    default:
      return parseInt(num, 10).toLocaleString('en-US')
  }
}

const getPicture = (row, isTeam) =>
  isTeam
    ? getEliteAgencyTeamLogo(row.u_picture)
    : getHeadshot(row.u_picture ? { u_picture: row.u_picture, id: row.user_id } : null)

const getDetail = (row, group) => (group === 'sub_agent_id' ? row.team_name || row.div_name : row.div_name)

// One ranked board, clipped to the rows that fit the screen (no scrolling on a TV).
const BigScreenBoard = ({ rows, unit, group, emptyMessage }) => {
  const listRef = useRef(null),
    rowRef = useRef(null),
    [fit, setFit] = useState(10)

  useLayoutEffect(() => {
    const list = listRef.current
    if (!list) return

    const measure = () => {
      const rowHeight = rowRef.current?.offsetHeight
      if (rowHeight > 0) setFit(Math.max(1, Math.floor(list.clientHeight / rowHeight)))
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(list)
    return () => observer.disconnect()
  }, [rows])

  // Rows can be taller than the first one measured (e.g. on a narrow screen); never show a cut-off row.
  useLayoutEffect(() => {
    const list = listRef.current,
      last = list?.lastElementChild
    if (fit > 1 && last && last.getBoundingClientRect().bottom > list.getBoundingClientRect().bottom + 1)
      setFit(fit - 1)
  }, [fit, rows])

  if (!rows.length)
    return (
      <div className='big-screen-board is-empty'>
        <p>{emptyMessage}</p>
      </div>
    )

  const isTeam = group === 'elite_agency_id'

  return (
    <ol className='big-screen-board' ref={listRef}>
      {rows.slice(0, fit).map((row, i) => (
        <li
          key={`${row.user_id || row.u_name}-${i}`}
          ref={i === 0 ? rowRef : undefined}
          className={`big-screen-row rank-${row.rank || i + 1}`}
        >
          <span className='big-screen-rank'>{row.rank || i + 1}</span>
          <img
            className='big-screen-picture'
            src={getPicture(row, isTeam)}
            alt=''
            onError={e => {
              e.currentTarget.onerror = null
              e.currentTarget.src = getHeadshot(null)
            }}
          />
          <span className='big-screen-identity'>
            <span className='big-screen-name'>{row.u_name}</span>
            <span className='big-screen-detail'>{getDetail(row, group)}</span>
          </span>
          <span className='big-screen-total'>{formatTotal(row.total, unit)}</span>
        </li>
      ))}
    </ol>
  )
}

export default BigScreenBoard
